import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';

import { Neutrals, Primary } from '../../color-palette/colors';

const SEGMENT_PADDING_X = 14;
const SEGMENT_PADDING_Y = 6;

export interface SegmentedOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  lang?: string;
  ariaLabel?: string;
}

export interface SegmentedControlProps {
  value: string;
  options: SegmentedOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
}

const groupSx =
  (fullWidth: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    bgcolor: Neutrals[50],
    borderRadius: 1.5,
    boxSizing: 'border-box',
    p: 1,
    gap: 1,
    width: fullWidth ? '100%' : 'auto',
    '& .MuiToggleButton-root': {
      flex: fullWidth ? 1 : 'initial',
      border: '1px solid transparent',
      borderRadius: 1,
      marginInlineStart: 0,
      textTransform: 'none',
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.label.fontSize,
      color: Neutrals[700],
      px: `${SEGMENT_PADDING_X}px`,
      py: `${SEGMENT_PADDING_Y}px`,
      '@media (pointer: coarse)': { minHeight: theme.controlHeight.medium, minWidth: theme.controlHeight.medium },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      '&.Mui-selected': {
        bgcolor: theme.palette.background.paper,
        color: Primary[900],
        borderColor: Primary[900],
        boxShadow: theme.customShadows.cardResting,
        '&:hover': { bgcolor: theme.palette.background.paper },
      },
    },
  });

export const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
  ({ value, options, onChange, disabled = false, size = 'small', fullWidth = false, 'aria-label': ariaLabel, sx }, ref) => (
    <ToggleButtonGroup
      ref={ref}
      exclusive
      value={value}
      size={size}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(_e, next) => {
        if (next != null) onChange(next as string);
      }}
      sx={[groupSx(fullWidth), ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {options.map((opt) => (
        <ToggleButton key={opt.value} value={opt.value} disabled={opt.disabled} lang={opt.lang} aria-label={opt.ariaLabel}>
          {opt.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  ),
);

SegmentedControl.displayName = 'SegmentedControl';
